// ============================================================================
// File: ComputeFootprintPanel.tsx — Spec 213 (US5) · reworked by Spec 215 US6
//   FR-016: plain-language title/caption (no constitution citations).
//   FR-017: interpretive context — backup/collection split, 14-day trend, and a
//           share-of-managed-data denominator; the title claims ONLY what the
//           data contains (collection reads appear when Spec 213 wires the writer).
// Author: Peter Dobler (Dobler Data Solutions)
// Created: 2026-07-03
// Feature: 215-admin-dashboard-remediation
// Constitution: v1.23.0 | Principle XLV (Design System)
// ============================================================================
// Self-fetching panel: GET /api/compute-footprint/summary → { rows, context }.
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, Cell, ReferenceLine, ResponsiveContainer, Tooltip,
} from 'recharts';
import { DashboardPanel, Badge, Spinner, chartTooltipStyle } from '@/components/ui';
import { Sparkline } from './Sparkline';
import { apiClient } from '../../../services/apiClient';
import { unmeasuredSizeNote } from '@/utils/unmeasuredSize';

interface FootprintSummaryRow {
  capacityId: string;
  capacityName: string;
  last24hBytes: number;
  budgetBytesPerDay: number;
  overBudget: boolean;
}

interface FootprintContext {
  bySource: { backupBytes: number; collectionBytes: number };
  trend14d: Array<{ day: string; totalBytes: number }>;
  managedBytesTotal: number;
  /** Spec 286 T159 item 32: lakehouses + warehouses in managedBytesTotal counted at their last known size. */
  sizeNotMeasuredCount?: number;
}

interface FootprintSummaryResponse {
  rows: FootprintSummaryRow[];
  context: FootprintContext | null;
}

/** Human-readable bytes (binary units). */
const fmtBytes = (n: number): string => {
  if (!n || n < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 || v >= 100 ? 0 : 1)} ${units[i]}`;
};

export const ComputeFootprintPanel: React.FC = () => {
  const { data, isLoading, isError } = useQuery<FootprintSummaryResponse>({
    queryKey: ['compute-footprint', 'summary'],
    queryFn: async () => {
      const res = await apiClient.get<FootprintSummaryResponse>('/api/compute-footprint/summary');
      return res.data;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const loading = isLoading || isError;
  const all = data?.rows ?? [];
  const context = data?.context ?? null;
  const rows = all.filter((r) => r.last24hBytes > 0).slice(0, 8); // busiest capacities
  const budget = rows[0]?.budgetBytesPerDay ?? all[0]?.budgetBytesPerDay ?? 0;
  const totalBytes = all.reduce((s, r) => s + r.last24hBytes, 0);
  const overCount = all.filter((r) => r.overBudget).length;

  // FR-017 honesty: only claim collection reads when a collection writer has
  // actually recorded any (Spec 213 owns that writer).
  const hasCollection = (context?.bySource.collectionBytes ?? 0) > 0;
  const title = hasCollection ? 'Data Read by Backups + Collection (24h)' : 'Data Read by Backups (24h)';

  // Denominator line: what share of everything under management was read.
  const managed = context?.managedBytesTotal ?? 0;
  const sharePct = managed > 0 ? (totalBytes / managed) * 100 : null;
  const shareText =
    sharePct == null ? null
    : sharePct < 0.01 ? '<0.01%'
    : `${sharePct.toFixed(2)}%`;
  // Spec 286 T159 item 32: the denominator keeps lakehouse and warehouse sizes the last collection did not measure.
  const managedNote = unmeasuredSizeNote(context?.sizeNotMeasuredCount);

  // 1024², not 1e6 — this panel's own fmtBytes is binary, so a decimal divisor made the trend
  // line disagree with the headline it sits under for the same underlying bytes.
  const trend = (context?.trend14d ?? []).map((d) => ({ date: d.day, value: Math.round(d.totalBytes / 1024 ** 2) }));

  const chartData = rows.map((r) => ({
    name: r.capacityName || `${r.capacityId.slice(0, 8)}…`,
    bytes: r.last24hBytes,
    over: r.overBudget,
  }));

  // Auto-scale the X axis to the DATA (not the budget). Otherwise a huge budget (e.g. the 1 TiB
  // placeholder) forces the domain and squashes real KB/MB reads to invisible slivers. Only draw
  // the budget line when it's actually in view; otherwise surface it as a caption.
  const dataMax = chartData.reduce((m, d) => Math.max(m, d.bytes), 0);
  const axisMax = Math.max(dataMax * 1.15, 1);
  const budgetInView = budget > 0 && budget <= axisMax;

  /**
   * The value migration-seeded for `footprint_budget_bytes_per_day`, whose own description in
   * dbo.global_settings reads: "1 TiB placeholder; tune against observed footprint."
   *
   * Treating it as a real threshold produced "14.1 TB · 2 over budget" on a day whose volume was
   * dominated by a one-off re-anchor — an alarm derived from a number nobody had chosen. Comparing
   * against the exact seeded constant (not a range) means the moment an operator sets any other
   * value, this flips to the real threshold behaviour with no further code change.
   */
  const BUDGET_PLACEHOLDER_BYTES = 1_099_511_627_776; // 1 TiB, as seeded
  const budgetIsPlaceholder = budget === BUDGET_PLACEHOLDER_BYTES;

  return (
    <DashboardPanel
      title={title}
      titleInfo="Bytes your backups (and collection) READ from OneLake in the last 24h, against the total under management. Reading is what a backup is — this is volume, not a bill. Since backups move server-side (Azure storage to Azure storage), those bytes never traverse our compute and incur no egress or NAT charge."
    >
      {loading ? (
        <div className="flex min-h-[100px] items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="flex h-full flex-col">
          <div className="mb-1 flex items-baseline gap-2">
            <span className="text-[length:var(--text-display)] font-bold leading-none text-[var(--color-text-primary)]">
              {fmtBytes(totalBytes)}
            </span>
            {/* 🛑 The budget is a SEEDED PLACEHOLDER until someone tunes it, and pricing a
                placeholder is how this tile read "14.1 TB · 2 over budget" on 2026-08-13 while
                the setting's own description in global_settings said "1 TiB placeholder; tune
                against observed footprint". A red badge against a number nobody chose is not a
                signal — it is a permanent false alarm, and permanent false alarms get ignored
                along with the real ones. The threshold mechanism is kept intact: set a real
                value and this badge starts meaning something on the next render. */}
            {budgetIsPlaceholder ? (
              <Badge variant="info" isOutlined>budget not set</Badge>
            ) : (
              <Badge variant={overCount > 0 ? 'error' : 'success'} isOutlined>
                {overCount > 0 ? `${overCount} over budget` : 'within budget'}
              </Badge>
            )}
          </div>
          <div className="text-xs text-[var(--color-text-secondary)]">
            Bytes {hasCollection ? 'backups and collection' : 'backups'} READ from OneLake in the
            last 24 hours{shareText ? <> — <span className="font-medium text-[var(--color-text-primary)]">{shareText}</span> of the {fmtBytes(managed)} under management{managedNote ? ` (lakehouses and warehouses: ${managedNote})` : ''}</> : ''}.
            {' '}Reading is what a backup is; these bytes move server-side and incur no egress or
            NAT charge.
            {budgetIsPlaceholder ? (
              <> Daily budget is still the seeded {fmtBytes(budget)} placeholder — tune it against
              observed volume before treating it as a threshold.</>
            ) : budget > 0 ? (
              <> Daily budget {fmtBytes(budget)}{!budgetInView ? ' (well above current)' : ''}.</>
            ) : null}
          </div>
          {hasCollection && context && (
            <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
              Backups {fmtBytes(context.bySource.backupBytes)} · collection {fmtBytes(context.bySource.collectionBytes)}
            </div>
          )}
          {trend.length > 1 && (
            <div data-testid="footprint-trend-14d">
              <Sparkline data={trend} color="#0ea5e9" valueFmt={(n) => `${n.toLocaleString()} MB`} label="read/day" />
            </div>
          )}

          <div className="mt-2 min-h-[140px] flex-1">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <XAxis type="number" domain={[0, axisMax]} tickFormatter={fmtBytes} hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  />
                  <Tooltip
                    {...chartTooltipStyle}
                    formatter={((v: unknown) => [fmtBytes(Number(v)), 'read (24h)']) as any}
                  />
                  {budgetInView && (
                    <ReferenceLine
                      x={budget}
                      stroke="var(--color-status-warning)"
                      strokeDasharray="3 3"
                      label={{ value: 'budget', fontSize: 10, fill: 'var(--color-status-warning)', position: 'top' }}
                    />
                  )}
                  <Bar dataKey="bytes" radius={[0, 4, 4, 0]} maxBarSize={22}>
                    {chartData.map((d, i) => (
                      <Cell key={i} fill={d.over ? 'var(--color-status-danger)' : 'var(--color-accent)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-xs text-[var(--color-text-secondary)]">
                No reads recorded in the last 24h (backups idle).
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardPanel>
  );
};

export default ComputeFootprintPanel;
